import { useState, useEffect, useCallback, useRef } from 'react'
import { BASE } from '../utils/api'

interface MediaFile {
  id: string
  filename: string
  url: string
  mimeType: string
  size: number
  createdAt: string
}

interface Props {
  onSelect?: (url: string) => void
  onClose: () => void
}

export function MediaLibraryModal({ onSelect, onClose }: Props) {
  const [files, setFiles] = useState<MediaFile[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<MediaFile | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const loadFiles = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const token = localStorage.getItem('bst-token') ?? ''
      const res = await fetch(`${BASE}/media`, { headers: { Authorization: `Bearer ${token}` } })
      if (!res.ok) throw new Error('Failed to load')
      const data = await res.json() as MediaFile[]
      setFiles(data)
    } catch {
      setError('Ошибка загрузки файлов')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadFiles() }, [loadFiles])

  async function upload(file: File) {
    setUploading(true)
    setError('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const token = localStorage.getItem('bst-token') ?? ''
      const res = await fetch(`${BASE}/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (!res.ok) throw new Error('Upload failed')
      const data = await res.json() as MediaFile
      setFiles(f => [data, ...f])
      setSelected(data)
    } catch {
      setError('Ошибка загрузки')
    } finally {
      setUploading(false)
    }
  }

  async function deleteFile(id: string) {
    setDeleting(id)
    try {
      const token = localStorage.getItem('bst-token') ?? ''
      await fetch(`${BASE}/media/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      setFiles(f => f.filter(x => x.id !== id))
      if (selected?.id === id) setSelected(null)
    } finally {
      setDeleting(null)
    }
  }

  function copyUrl(url: string) {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes}B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '16px', width: '920px', maxWidth: '96vw', maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>Медиатека</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
              {loading ? 'Загрузка...' : `${files.length} файлов`}
            </div>
          </div>
          <label
            style={{ padding: '7px 16px', background: uploading ? 'var(--bg-3)' : 'var(--accent)', color: uploading ? 'var(--text-muted)' : '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: uploading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {uploading ? 'Загрузка...' : '+ Загрузить'}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = '' }} />
          </label>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {error && (
          <div style={{ padding: '8px 20px', background: '#fee2e2', color: '#991b1b', fontSize: '12px', flexShrink: 0 }}>{error}</div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

          {/* Grid */}
          <div
            style={{ flex: 1, overflowY: 'auto', padding: '16px', borderRight: selected ? '1px solid var(--border)' : 'none' }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.type.startsWith('image/')) upload(f) }}
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>Загрузка...</div>
            ) : files.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🖼</div>
                <div>Нет файлов. Загрузите первый!</div>
                <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.6 }}>Перетащите сюда изображения</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
                {files.map(f => (
                  <div
                    key={f.id}
                    onClick={() => setSelected(s => s?.id === f.id ? null : f)}
                    style={{
                      position: 'relative', borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                      border: `2px solid ${selected?.id === f.id ? 'var(--accent)' : 'transparent'}`,
                      aspectRatio: '1', background: 'var(--bg-2)',
                    }}
                  >
                    <img src={f.url} alt={f.filename} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <button
                      onClick={e => { e.stopPropagation(); deleteFile(f.id) }}
                      disabled={deleting === f.id}
                      style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', borderRadius: '4px', width: '20px', height: '20px', cursor: 'pointer', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0 }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                      onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                      title="Удалить"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detail pane */}
          {selected && (
            <div style={{ width: '240px', overflowY: 'auto', padding: '16px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <img src={selected.url} alt={selected.filename} style={{ width: '100%', borderRadius: '8px', objectFit: 'contain', maxHeight: '160px', background: 'var(--bg-2)' }} />
              <div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Файл</div>
                <div style={{ fontSize: '12px', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{selected.filename}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Размер</div>
                <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{formatSize(selected.size)}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>URL</div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--bg-3)', borderRadius: '6px', padding: '6px 8px', wordBreak: 'break-all', fontFamily: 'monospace' }}>{selected.url}</div>
              </div>
              <button
                onClick={() => copyUrl(selected.url)}
                style={{ padding: '7px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', fontWeight: 600, color: copied ? '#22c55e' : 'var(--text-secondary)', cursor: 'pointer' }}
              >{copied ? '✓ Скопировано' : 'Копировать URL'}</button>
              {onSelect && (
                <button
                  onClick={() => { onSelect(selected.url); onClose() }}
                  style={{ padding: '9px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                >Вставить в блок</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
